import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { createPortal } from 'react-dom';
import { ArrowRight, ChevronRight, Download, MessageSquare } from 'lucide-react';
import { moodDashboardAPI, employeeRemarksAPI, type DashRichFilterParams } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import EmployeeRemarksModal from '../EmployeeRemarksModal';
import { buildCsv, downloadCsv } from '../../utils/csv';
import { Modal, SearchBox, StackedBarChart, fmtCount, type BarSegment } from './DashboardPrimitives';
import { BlockLoader, Spinner } from '../loading';
import { useTableSort, SortTh } from './useTableSort';
import DrillLink from './DrillLink';
import { DRILL_ROUTES, buildDrillUrl, monthRange } from '../../utils/drilldown';
import { useDashboardList } from './useDashboardList';
import { MOOD_LIST, readWatchCounts } from './lists/mood';

// ─────────────────────────────────────────────────────────────────────────────
// Types (mirror the dashboard-mood.controller responses)

type MoodKey = 'very_unhappy' | 'unhappy' | 'neutral' | 'happy' | 'very_happy';

interface MoodDay {
  date: string;
  label: number;
  very_unhappy: number;
  unhappy: number;
  neutral: number;
  happy: number;
  very_happy: number;
  submitted: number;
  expected: number;
  avgScore: number | null;
}

interface TrendData {
  month: { ym: string; label: string; startDate: string; endDate: string; daysInMonth: number; isCurrentMonth: boolean };
  employeeCount: number;
  daily: MoodDay[];
  averages: {
    avgMood: number | null;
    positivePct: number | null;
    negativePct: number | null;
    neutralPct: number | null;
    participationPct: number | null;
    responses: number;
  };
  distribution: Record<MoodKey, number>;
  byManager: MoodManagerRow[];
  /** By Assistant Manager / by Trade Head. Absent from an older API, and empty with
   *  tiers.available false for a project defining neither role. */
  byAm?: MoodManagerRow[];
  byTh?: MoodManagerRow[];
  tiers?: { available: boolean; partial: boolean };
  prevAvgMood: number | null;
}

// Per-manager mood roll-up — the Manager-level comparison table.
interface MoodManagerRow {
  managerCode: string | null;
  managerName: string;
  employeeCount: number;
  responses: number;
  avgMood: number | null;
  positivePct: number | null;
  negativePct: number | null;
  participationPct: number | null;
}

interface LowMoodRow {
  emp_code: string;
  employee: string;
  store: string;
  city: string;
  mobile: string;
  negativeDays: number;
  submitted: number;
  avgScore: number | null;
  latestMood: MoodKey | null;
  latestMoodLabel: string | null;
  latestMoodDate: string | null;
  // Performance scorecard (from monthly_scorecard_totals) — same values as the
  // Deployment → Store Performance table: selected-month score + 6-month average.
  monthScore: number | null;
  avg6mScore: number | null;
}

interface NotSubmittedRow {
  emp_code: string;
  employee: string;
  store: string;
  city: string;
  mobile: string;
}

interface WatchlistData {
  month: { ym: string; label: string };
  today: string;
  lowMood: { total: number; rows: LowMoodRow[] };
  notSubmittedToday: { total: number; rows: NotSubmittedRow[] };
}

interface TrendDayRow {
  emp_code: string;
  employee: string;
  store: string;
  city: string;
  mood: MoodKey | null;
  moodLabel: string;
}

// ─────────────────────────────────────────────────────────────────────────────
// Mood taxonomy — colours match the mobile mood picker (red → green). Segments
// are listed best-first so the green "Very happy" sits at the base of each bar
// (StackedBarChart renders the first segment at the bottom).

const MOOD_HEX: Record<MoodKey, string> = {
  very_happy: '#10b981',
  happy: '#84cc16',
  neutral: '#f59e0b',
  unhappy: '#f97316',
  very_unhappy: '#ef4444',
};
const MOOD_SEGMENTS: BarSegment[] = [
  { key: 'very_happy', label: 'Very happy', hex: MOOD_HEX.very_happy },
  { key: 'happy', label: 'Happy', hex: MOOD_HEX.happy },
  { key: 'neutral', label: 'Neutral', hex: MOOD_HEX.neutral },
  { key: 'unhappy', label: 'Unhappy', hex: MOOD_HEX.unhappy },
  { key: 'very_unhappy', label: 'Very unhappy', hex: MOOD_HEX.very_unhappy },
];
// 'YYYY-MM-DD' → "Today" / "Yesterday" / "12 Jun". Parsed as parts (not `new Date(str)`)
// so the date-only string can't shift a day via UTC parsing.
const fmtMoodDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const [y, m, d] = String(iso).split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d);
  if (Number.isNaN(dt.getTime())) return '';
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((midnight(new Date()) - midnight(dt)) / 86400000);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

const MOOD_LABEL: Record<MoodKey, string> = {
  very_happy: 'Very happy', happy: 'Happy', neutral: 'Neutral', unhappy: 'Unhappy', very_unhappy: 'Very unhappy',
};

const moodCount = (day: MoodDay, key: string): number => (day as unknown as Record<string, number>)[key] || 0;
const pct = (v: number | null): string => (v == null ? '—' : `${v}%`);

// ─────────────────────────────────────────────────────────────────────────────

/**
 * One mood roll-up table — By Assistant Manager, By Trade Head, or (for a project without
 * those roles) the reporting-manager comparison. Each instance owns its sort state.
 *
 * `linkByName` drills to the Mood report filtered to the row's NAME, which that report
 * matches against each employee's DIRECT reporting head. So it is only offered where the
 * row's team is that person's direct reports — a manager, or an AM. A Trade Head's direct
 * reports are AMs, so their link would open on the wrong people; theirs is plain text.
 */
function MoodManagerTable({
  rows,
  nameLabel,
  linkByName,
  month,
  monthYm,
  compact = false,
}: {
  rows: MoodManagerRow[];
  nameLabel: string;
  linkByName: boolean;
  month?: TrendData['month'];
  monthYm: string;
  /** Tighter columns for the side-by-side AM / Trade Head cards, where the full-width
   *  padding pushed Negative behind the card's scroll at 1440px. */
  compact?: boolean;
}) {
  const pr = compact ? 'pr-3' : 'pr-4';
  const sort = useTableSort(rows, {
    manager: (m) => m.managerName,
    team: (m) => m.employeeCount,
    responses: (m) => m.responses,
    avgMood: (m) => m.avgMood,
    participation: (m) => m.participationPct,
    positive: (m) => m.positivePct,
    negative: (m) => m.negativePct,
  });
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="text-left text-xs font-semibold uppercase tracking-wide text-gray-400">
          <tr>
            <SortTh label={nameLabel} sortKey="manager" sort={sort} className={`whitespace-nowrap py-2 ${pr}`} />
            <SortTh label="Team" sortKey="team" sort={sort} align="right" className={`whitespace-nowrap py-2 ${pr}`} />
            <SortTh label="Responses" sortKey="responses" sort={sort} align="right" className={`whitespace-nowrap py-2 ${pr}`} />
            <SortTh label="Avg mood" sortKey="avgMood" sort={sort} align="right" className={`whitespace-nowrap py-2 ${pr}`} />
            <SortTh label="Participation" sortKey="participation" sort={sort} align="right" className={`whitespace-nowrap py-2 ${pr}`} />
            <SortTh label="Positive" sortKey="positive" sort={sort} align="right" className={`whitespace-nowrap py-2 ${pr}`} />
            <SortTh label="Negative" sortKey="negative" sort={sort} align="right" className="whitespace-nowrap py-2" />
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {sort.sorted.map((m) => (
            <tr key={m.managerCode ?? '—'} className="hover:bg-gray-50">
              <td className={`whitespace-nowrap py-2 ${pr} font-medium text-gray-800`}>
                {/* Drill-down: the Mood report filtered to this person's direct team over
                    the selected month (see linkByName above). The no-manager bucket has
                    nobody to filter by → plain text, no link. */}
                {linkByName && m.managerCode ? (
                  <DrillLink
                    href={buildDrillUrl(DRILL_ROUTES.mood, {
                      manager: m.managerName,
                      ...(month ? { startDate: month.startDate, endDate: month.endDate } : monthRange(monthYm)),
                    })}
                    title={`Open Mood Check for ${m.managerName}'s team${month?.label ? ` — ${month.label}` : ''}`}
                  >
                    {m.managerName}
                  </DrillLink>
                ) : (
                  m.managerName
                )}
              </td>
              <td className={`py-2 ${pr} text-right text-gray-600`}>{m.employeeCount}</td>
              <td className={`py-2 ${pr} text-right text-gray-600`}>{m.responses.toLocaleString()}</td>
              <td className={`py-2 ${pr} text-right font-semibold ${m.avgMood == null ? 'text-gray-400' : m.avgMood < 3 ? 'text-rose-600' : m.avgMood < 3.5 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {m.avgMood == null ? '—' : `${m.avgMood} / 5`}
              </td>
              <td className={`py-2 ${pr} text-right text-gray-700`}>{pct(m.participationPct)}</td>
              <td className={`py-2 ${pr} text-right text-emerald-600`}>{pct(m.positivePct)}</td>
              <td className="py-2 text-right text-rose-600">{pct(m.negativePct)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MoodTab({ projectId, month, roleId, filters }: { projectId: number | null; month: string; roleId?: string; filters?: DashRichFilterParams }) {
  const monthYm = month;

  const [trend, setTrend] = useState<TrendData | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);

  const [watch, setWatch] = useState<WatchlistData | null>(null);
  const [watchLoading, setWatchLoading] = useState(false);
  const [watchError, setWatchError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Set<string>>(new Set());

  // Drill-down (clicked bar segment)
  const [drillOpen, setDrillOpen] = useState(false);
  const [drillDate, setDrillDate] = useState('');
  const [drillLabel, setDrillLabel] = useState('');
  const [drillRows, setDrillRows] = useState<TrendDayRow[]>([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillSearch, setDrillSearch] = useState('');

  // The two full lists open by address (?list=): a card's number and "View All" both
  // write it, and what is open is read from it on every render. Held in state it would
  // be shut by the reload that follows the default Role filter.
  const { list, openList, closeList } = useDashboardList();

  // Per-employee remarks (call notes) — opened from the low-mood watchlist.
  const user = useAuthStore((s) => s.user);
  const [remarksFor, setRemarksFor] = useState<{ code: string; name: string } | null>(null);
  const [remarkCounts, setRemarkCounts] = useState<Record<string, number>>({});

  const trendReq = useRef(0);
  const watchReq = useRef(0);
  const drillReq = useRef(0);

  // ── Fetch trend on month / project / role change ──
  useEffect(() => {
    const reqId = ++trendReq.current;
    setTrendLoading(true);
    setTrendError(null);
    moodDashboardAPI
      .getTrend({ month: monthYm, projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== trendReq.current) return;
        setTrend((res.data?.data ?? null) as TrendData | null);
      })
      .catch((err) => {
        if (reqId !== trendReq.current) return;
        setTrendError(err.response?.data?.message || 'Failed to load mood trend');
        setTrend(null);
      })
      .finally(() => {
        if (reqId === trendReq.current) setTrendLoading(false);
      });
  }, [monthYm, projectId, roleId, filters]);

  // ── Fetch watchlist on month / project / role change ──
  useEffect(() => {
    const reqId = ++watchReq.current;
    setWatchLoading(true);
    setWatchError(null);
    moodDashboardAPI
      .getWatchlist({ month: monthYm, projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
      .then((res) => {
        if (reqId !== watchReq.current) return;
        setWatch((res.data?.data ?? null) as WatchlistData | null);
      })
      .catch((err) => {
        if (reqId !== watchReq.current) return;
        setWatchError(err.response?.data?.message || 'Failed to load watchlist');
        setWatch(null);
      })
      .finally(() => {
        if (reqId === watchReq.current) setWatchLoading(false);
      });
  }, [monthYm, projectId, roleId, filters]);

  // Remark counts for the low-mood watchlist rows → "N notes" badge on each row.
  const watchCodesKey = useMemo(
    () => [...new Set((watch?.lowMood.rows ?? []).map((r) => r.emp_code).filter(Boolean))].join(','),
    [watch],
  );
  useEffect(() => {
    if (!watchCodesKey) { setRemarkCounts({}); return; }
    let cancelled = false;
    employeeRemarksAPI
      .counts({ employeeCodes: watchCodesKey, projectId: projectId ?? undefined })
      .then((res) => { if (!cancelled) setRemarkCounts(res.data?.data?.counts || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [watchCodesKey, projectId]);

  // ── The two full lists: only from a watchlist answer that has arrived ──
  // While the call is on its way, or after it failed, nothing is counted and no list is
  // shown: a number that is not known is never drawn as zero.
  const watchLists = !watchLoading && !watchError ? watch : null;
  const watchCounts = useMemo(() => readWatchCounts(watchLists), [watchLists]);

  const visibleKeys = useMemo<Set<string>>(() => {
    if (selected.size > 0) return new Set(selected);
    return new Set(MOOD_SEGMENTS.map((s) => s.key));
  }, [selected]);

  const toggleSegment = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ── Drill-down ──
  const openDrill = useCallback(
    (date: string, moodKey: string, label: string) => {
      setDrillOpen(true);
      setDrillDate(date);
      setDrillLabel(label);
      setDrillRows([]);
      setDrillSearch('');
      setDrillLoading(true);
      const reqId = ++drillReq.current;
      moodDashboardAPI
        .getTrendDay({ date, mood: moodKey, projectId: projectId ?? undefined, roleId: roleId || undefined, ...filters })
        .then((res) => {
          if (reqId !== drillReq.current) return;
          setDrillRows((res.data.data?.rows as TrendDayRow[]) || []);
        })
        .catch(() => {
          if (reqId === drillReq.current) setDrillRows([]);
        })
        .finally(() => {
          if (reqId === drillReq.current) setDrillLoading(false);
        });
    },
    [projectId, roleId, filters],
  );

  const filteredDrillRows = useMemo(() => {
    const q = drillSearch.trim().toLowerCase();
    if (!q) return drillRows;
    return drillRows.filter((r) => [r.employee, r.emp_code, r.store, r.city].some((v) => (v || '').toLowerCase().includes(q)));
  }, [drillRows, drillSearch]);

  // ── Distribution (month totals → shares) ──
  const distribution = useMemo(() => {
    const d = trend?.distribution;
    const total = d ? MOOD_SEGMENTS.reduce((acc, s) => acc + (d[s.key as MoodKey] || 0), 0) : 0;
    return { total, counts: d };
  }, [trend]);

  // ── CSV download (day-wise mood counts) ──
  const handleDownload = () => {
    if (!trend) return;
    const headers = ['Date', 'Very happy', 'Happy', 'Neutral', 'Unhappy', 'Very unhappy', 'Submitted', 'Avg mood (1-5)'];
    const rows = trend.daily.map((d) => [
      d.date,
      fmtCount(d.very_happy),
      fmtCount(d.happy),
      fmtCount(d.neutral),
      fmtCount(d.unhappy),
      fmtCount(d.very_unhappy),
      fmtCount(d.submitted),
      d.avgScore == null ? '' : String(d.avgScore),
    ]);
    downloadCsv(`Mood_Trend_${monthYm}.csv`, buildCsv(headers, rows));
  };

  // ── CSV download (full low-mood watchlist — every employee, not just top 10) ──
  const handleWatchlistDownload = async () => {
    const rows = watch?.lowMood.rows ?? [];
    if (rows.length === 0) return;

    // Pull the actual remark TEXT (call notes) for every listed employee so the export
    // carries the notes themselves — the on-screen table only shows a count. Newest-first
    // per code (the bulk endpoint's order), all of an employee's notes joined into one
    // cell. Fail-soft: a fetch error still downloads the file, just with the notes blank.
    const codes = [...new Set(rows.map((r) => r.emp_code).filter(Boolean))];
    let remarksByCode: Record<string, Array<{ body: string; author: string | null; date: string | null }>> = {};
    if (codes.length > 0) {
      try {
        const res = await employeeRemarksAPI.bulk({ employeeCodes: codes, projectId: projectId ?? undefined });
        remarksByCode = res.data?.data?.remarks || {};
      } catch (err) {
        console.error('Failed to fetch remark notes for watchlist export:', err);
      }
    }
    // Each note rendered as "[date · author] body"; notes for one employee separated by
    // " | " (buildCsv folds any newline inside a note to a space, so the separator is what
    // keeps them distinguishable in a single cell).
    const formatNotes = (code: string): string =>
      (remarksByCode[code] || [])
        .map((n) => {
          const meta = [n.date, n.author].filter(Boolean).join(' · ');
          return meta ? `[${meta}] ${n.body}` : n.body;
        })
        .join('  |  ');

    const headers = [
      'Employee', 'Emp Code', 'Store', 'City', 'Mobile Number', 'Unhappy days',
      'Avg mood (1-5)', 'Latest mood', 'Latest mood date', 'Month score', '6-mo Avg', 'Remarks', 'Remark Notes',
    ];
    const data = rows.map((r) => [
      r.employee,
      r.emp_code,
      r.store || '',
      r.city || '',
      r.mobile || '',
      String(r.negativeDays),
      r.avgScore == null ? '' : String(r.avgScore),
      r.latestMoodLabel || '',
      r.latestMoodDate || '',
      r.monthScore == null ? '' : String(r.monthScore),
      r.avg6mScore == null ? '' : String(r.avg6mScore),
      String(remarkCounts[r.emp_code] || 0),
      formatNotes(r.emp_code),
    ]);
    downloadCsv(`Mood_Low_Watchlist_${monthYm}.csv`, buildCsv(headers, data));
  };

  const responses = trend?.averages.responses ?? 0;
  const hasData = !!trend && responses > 0;

  // ── Manager-level comparison: By Assistant Manager + By Trade Head when the project
  // defines those roles, else the reporting-manager table. Each sorts inside
  // MoodManagerTable; rows arrive lowest-mood first.
  const managerRows = trend?.byManager ?? [];
  const amRows = trend?.byAm ?? [];
  const thRows = trend?.byTh ?? [];
  const tiersOn = !!trend?.tiers?.available;

  // ── Sorting: drill-down (day + mood) modal ──
  const drillSort = useTableSort(filteredDrillRows, {
    employee: (r) => r.employee,
    store: (r) => r.store,
    city: (r) => r.city,
    mood: (r) => r.moodLabel,
  });

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* ─── Module 1: Mood Trend ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] md:items-center">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Mood Trend</h2>
            <p className="text-xs text-gray-500">Date-wise mood mix for the selected month</p>
          </div>
          <div className="flex items-center justify-center gap-6 text-center">
            <div className="flex flex-col">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">Average mood</span>
              <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900">
                {trend?.averages.avgMood == null ? '—' : `${trend.averages.avgMood} / 5`}
              </span>
            </div>
            <div className="h-8 w-px bg-gray-200" aria-hidden="true" />
            <div className="flex flex-col">
              <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-gray-500">Participation</span>
              <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900">{pct(trend?.averages.participationPct ?? null)}</span>
            </div>
          </div>
          <div className="flex items-center justify-start gap-2 md:justify-end">
            <button
              type="button"
              onClick={handleDownload}
              disabled={!hasData || trendLoading}
              className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Download
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-600">
          <span className="font-medium">Employees in scope: {trend?.employeeCount ?? '—'}</span>
          {trend && <span>Range: {trend.month.startDate} to {trend.month.endDate}</span>}
        </div>

        {/* Chart */}
        <div className="mt-4">
          {trendLoading ? (
            <BlockLoader className="h-72" />
          ) : trendError ? (
            <div className="flex h-72 items-center justify-center text-sm text-rose-600">{trendError}</div>
          ) : !hasData ? (
            <div className="flex h-72 items-center justify-center text-sm text-gray-500">No mood check-ins for this month.</div>
          ) : (
            <StackedBarChart
              days={trend!.daily}
              segments={MOOD_SEGMENTS}
              visibleKeys={visibleKeys}
              getCount={(day, key) => moodCount(day, key)}
              getDayTotal={(day) => day.submitted}
              onSegmentClick={(day, seg) => openDrill(day.date, seg.key, seg.label)}
              tooltipLines={(day, _seg, count, dayTotal) => [
                `Count: ${fmtCount(count)}`,
                `Share: ${dayTotal > 0 ? Number(((count / dayTotal) * 100).toFixed(1)) : 0}%`,
                `Day avg: ${day.avgScore == null ? '—' : `${day.avgScore} / 5`}`,
              ]}
            />
          )}
        </div>

        {/* Legend (multi-select filter) */}
        {hasData && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            {MOOD_SEGMENTS.map((s) => {
              const isSelected = selected.has(s.key);
              const dimmed = selected.size > 0 && !isSelected;
              return (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => toggleSegment(s.key)}
                  className={`inline-flex items-center gap-1.5 text-xs ${
                    dimmed ? 'text-gray-400 opacity-60' : isSelected ? 'font-semibold text-gray-900' : 'text-gray-600'
                  }`}
                >
                  <span
                    className={`h-3 w-3 rounded-sm ${isSelected ? 'ring-2 ring-gray-500 ring-offset-1' : ''}`}
                    style={{ backgroundColor: s.hex }}
                  />
                  {s.label}
                </button>
              );
            })}
            {selected.size > 0 && (
              <button type="button" onClick={() => setSelected(new Set())} className="text-xs font-medium text-primary-600 hover:underline">
                Clear all
              </button>
            )}
          </div>
        )}
      </section>

      {/* ─── Module 2: Mood distribution ─── */}
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <h3 className="text-lg font-semibold text-gray-900">Mood distribution</h3>
        <p className="text-xs text-gray-500">How the team felt across {responses.toLocaleString()} check-ins this month</p>
        {distribution.total > 0 && distribution.counts ? (
          <>
            <div className="mt-3 flex h-6 overflow-hidden rounded-md">
              {MOOD_SEGMENTS.map((s) => {
                const c = distribution.counts![s.key as MoodKey] || 0;
                const w = (c / distribution.total) * 100;
                if (w <= 0) return null;
                return <div key={s.key} title={`${s.label}: ${c}`} style={{ width: `${w}%`, backgroundColor: s.hex }} />;
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-gray-600">
              {MOOD_SEGMENTS.map((s) => {
                const c = distribution.counts![s.key as MoodKey] || 0;
                const share = Number(((c / distribution.total) * 100).toFixed(1));
                return (
                  <span key={s.key} className="inline-flex items-center gap-1.5">
                    <span className="h-3 w-3 rounded-sm" style={{ backgroundColor: s.hex }} />
                    {s.label} <span className="font-semibold text-gray-800">{share}%</span>
                  </span>
                );
              })}
            </div>
          </>
        ) : (
          <div className="py-8 text-center text-sm text-gray-500">No check-ins to summarise.</div>
        )}
      </section>

      {/* ─── Manager-level comparison ─── */}
      {/* By Assistant Manager and By Trade Head, side by side, when the project defines
          those roles. Keyed on each employee's DIRECT manager (the fallback below), the
          table also listed Trade Heads — whose direct reports are AMs — admins and
          one-person teams alongside the real AM teams. */}
      {tiersOn ? (
        (amRows.length > 0 || thRows.length > 0) && (
          <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
            <section className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">By Assistant Manager</h3>
              <p className="text-xs text-gray-500">Average mood + participation of each Assistant Manager's team — lowest mood first</p>
              <MoodManagerTable compact rows={amRows} nameLabel="Assistant Manager" linkByName month={trend?.month} monthYm={monthYm} />
            </section>
            <section className="min-w-0 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900">By Trade Head</h3>
              <p className="text-xs text-gray-500">Every Assistant Manager's team under each Trade Head, combined — lowest mood first</p>
              <MoodManagerTable compact rows={thRows} nameLabel="Trade Head" linkByName={false} month={trend?.month} monthYm={monthYm} />
            </section>
            {trend?.tiers?.partial && (
              <p className="text-[11px] text-amber-700 lg:col-span-2">
                Some reporting lines couldn't be loaded, so a few teams may be counted under "No Assistant Manager" or
                "No Trade Head". Refresh in a moment.
              </p>
            )}
          </div>
        )
      ) : (
        managerRows.length > 0 && (
          <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <h3 className="text-lg font-semibold text-gray-900">Manager-level comparison</h3>
            <p className="text-xs text-gray-500">Average mood + participation by reporting manager — lowest mood first</p>
            <MoodManagerTable rows={managerRows} nameLabel="Manager" linkByName month={trend?.month} monthYm={monthYm} />
          </section>
        )
      )}

      {/* ─── Module 3 + 4: Low-mood watchlist + Not submitted today ─── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <MoodListCard
          title="Low-mood watchlist"
          subtitle="2+ unhappy days this month — worth a check-in"
          variant="watchlist"
          loading={watchLoading}
          error={watchError}
          rows={watch?.lowMood.rows ?? []}
          total={watch?.lowMood.total ?? 0}
          count={watchCounts.lowMood}
          onViewAll={() => openList(MOOD_LIST.lowMood)}
          onDownload={handleWatchlistDownload}
          remarkCounts={remarkCounts}
          onOpenRemarks={(code, name) => setRemarksFor({ code, name })}
        />
        <MoodListCard
          title="Not submitted today"
          subtitle="Active employees who haven't logged a mood today"
          variant="not_submitted"
          loading={watchLoading}
          error={watchError}
          rows={watch?.notSubmittedToday.rows ?? []}
          total={watch?.notSubmittedToday.total ?? 0}
          count={watchCounts.notSubmitted}
          onViewAll={() => openList(MOOD_LIST.notSubmitted)}
        />
      </div>

      {/* Drill-down modal */}
      {drillOpen &&
        createPortal(
          <Modal onClose={() => setDrillOpen(false)} title={`${drillLabel} — ${drillDate}`}>
            <div className="mb-3">
              <SearchBox value={drillSearch} onChange={setDrillSearch} placeholder="Search by name, code, store…" />
            </div>
            {drillLoading ? (
              <div className="flex h-40 items-center justify-center" role="status" aria-label="Loading">
                <Spinner size="md" />
              </div>
            ) : filteredDrillRows.length === 0 ? (
              <div className="py-10 text-center text-sm text-gray-500">No employees.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[560px] text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                      <SortTh label="Employee" sortKey="employee" sort={drillSort} className="pb-3 font-semibold" />
                      <SortTh label="Store" sortKey="store" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="City" sortKey="city" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                      <SortTh label="Mood" sortKey="mood" sort={drillSort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                    </tr>
                  </thead>
                  <tbody>
                    {drillSort.sorted.map((r) => (
                      <tr key={r.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                        <td className="py-3 font-medium text-gray-900">
                          {r.employee}
                          <span className="ml-1 text-xs text-gray-400">({r.emp_code})</span>
                        </td>
                        <td className="py-3 pl-4 text-gray-700">{r.store || '—'}</td>
                        <td className="py-3 pl-4 text-gray-700">{r.city || '—'}</td>
                        <td className="py-3 pl-4"><MoodBadge moodKey={r.mood} label={r.moodLabel} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Modal>,
          document.body,
        )}

      {/* View-all modals: open while the address names them and the watchlist answer
          has arrived; closing takes the name out of the address. */}
      {list === MOOD_LIST.lowMood && watchLists &&
        createPortal(
          <LowMoodModal
            rows={watchLists.lowMood.rows}
            onClose={closeList}
            remarkCounts={remarkCounts}
            onOpenRemarks={(code, name) => setRemarksFor({ code, name })}
          />,
          document.body,
        )}
      {list === MOOD_LIST.notSubmitted && watchLists &&
        createPortal(<NotSubmittedModal rows={watchLists.notSubmittedToday.rows} onClose={closeList} />, document.body)}

      <EmployeeRemarksModal
        open={!!remarksFor}
        onClose={() => setRemarksFor(null)}
        employeeCode={remarksFor?.code || ''}
        employeeName={remarksFor?.name}
        projectId={projectId ?? undefined}
        authorName={user?.fullName || undefined}
        onChanged={() => {
          if (remarksFor) setRemarkCounts((p) => ({ ...p, [remarksFor.code]: (p[remarksFor.code] || 0) + 1 }));
        }}
      />
    </div>
  );
}


// A small coloured pill for a mood.
function MoodBadge({ moodKey, label }: { moodKey: MoodKey | null; label: string }) {
  if (!moodKey) return <span className="text-gray-400">{label || '—'}</span>;
  const hex = MOOD_HEX[moodKey];
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium" style={{ backgroundColor: `${hex}1a`, color: hex }}>
      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: hex }} />
      {label || MOOD_LABEL[moodKey]}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// A list card's headline number: how many people the full list holds, and it says
// "people". Above zero it is a button that opens that list (the chevron says so, as on a
// headline tile); at zero it is plain. The card does not draw it at all while the count
// is not known.

function ListCount({ name, count, onOpen }: { name: string; count: number; onOpen: () => void }): ReactElement {
  const value = count.toLocaleString('en-IN');
  const unit = count === 1 ? 'person' : 'people';
  const body = (
    <>
      <span className="text-3xl font-bold tabular-nums tracking-tight text-gray-900 transition group-hover:text-primary-600">{value}</span>
      <span className="text-xs font-medium text-gray-500">{unit}</span>
    </>
  );
  if (count <= 0) return <span className="inline-flex items-baseline gap-1.5 whitespace-nowrap">{body}</span>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`${name}: ${value} ${unit}. Open the full list`}
      className="group inline-flex items-baseline gap-1.5 whitespace-nowrap rounded-md transition focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
    >
      {body}
      <ChevronRight className="h-3.5 w-3.5 self-center text-gray-300 transition group-hover:text-primary-500" aria-hidden />
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Card (top 10) for the low-mood watchlist / not-submitted-today

function MoodListCard({
  title,
  subtitle,
  variant,
  loading,
  error,
  rows,
  total,
  count,
  onViewAll,
  onDownload,
  remarkCounts,
  onOpenRemarks,
}: {
  title: string;
  subtitle: string;
  variant: 'watchlist' | 'not_submitted';
  loading: boolean;
  error: string | null;
  rows: (LowMoodRow | NotSubmittedRow)[];
  total: number;
  /** People on the full list, for the headline number; null while it is not known. */
  count: number | null;
  onViewAll: () => void;
  onDownload?: () => void;
  remarkCounts?: Record<string, number>;
  onOpenRemarks?: (code: string, name: string) => void;
}) {
  const top = rows.slice(0, 10);
  const sort = useTableSort(top, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    metric: (r) => (variant === 'watchlist' ? (r as LowMoodRow).negativeDays : null),
    latestMood: (r) => (variant === 'watchlist' ? ((r as LowMoodRow).latestMoodDate || '') : null),
    score: (r) => (variant === 'watchlist' ? (r as LowMoodRow).monthScore : null),
    avg6m: (r) => (variant === 'watchlist' ? (r as LowMoodRow).avg6mScore : null),
    // Remarks = the remark count on the button ("Add" = none yet = 0).
    remarks: (r) => (variant === 'watchlist' && onOpenRemarks ? (remarkCounts?.[r.emp_code] ?? 0) : null),
    // Status is the same "Not logged today" label on every not-submitted row.
    status: () => (variant === 'not_submitted' ? 'Not logged today' : null),
  });
  return (
    <section className="flex min-h-[430px] flex-col rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      {/* The header carries the list's headline number too. The title gives way first;
          on a narrow screen the number and buttons drop under it. They wrap in turn
          (never shrink-0): at phone width a four-digit number, Download and View All are
          wider than the card, and the number then sits above the buttons. */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
        <div className="min-w-0 flex-1 basis-48">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className="text-xs text-gray-500">{subtitle}</p>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
          {count !== null && <ListCount name={title} count={count} onOpen={onViewAll} />}
          {total > 0 && (
            <div className="flex items-center gap-2">
              {onDownload && (
                <button
                  type="button"
                  onClick={onDownload}
                  className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
                  title="Download the full list as CSV"
                >
                  <Download className="h-3.5 w-3.5" /> Download
                </button>
              )}
              <button
                type="button"
                onClick={onViewAll}
                className="inline-flex items-center gap-1 rounded-full border border-primary-200 px-3 py-1 text-xs font-medium text-primary-600 hover:bg-primary-50"
              >
                View All <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>

      {loading ? (
        <BlockLoader className="flex-1 py-0" />
      ) : error ? (
        <div className="flex flex-1 items-center justify-center text-sm text-rose-600">{error}</div>
      ) : top.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm text-gray-500">
          {variant === 'watchlist' ? 'No low-mood employees this month.' : 'Everyone has logged their mood today.'}
        </div>
      ) : (
        // overflow-x-auto: this card sits in a half-width grid column, so a wide table
        // must scroll INSIDE the card instead of pushing columns out of it.
        <div className="-mx-1 overflow-x-auto px-1">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
              <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
              <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              {variant === 'watchlist' ? (
                <>
                  <SortTh label="Unhappy days" sortKey="metric" sort={sort} align="right" className="min-w-[110px] whitespace-nowrap pb-3 pl-4 font-semibold" />
                  <SortTh label="Latest mood" sortKey="latestMood" sort={sort} title="The most recent mood this employee logged in the selected month" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                  <SortTh label="6-mo Avg" sortKey="avg6m" sort={sort} align="right" title="6-month average scorecard score" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                  <SortTh label="Remarks" sortKey="remarks" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 text-right font-semibold" />
                </>
              ) : (
                <SortTh label="Status" sortKey="status" sort={sort} align="right" className="min-w-[130px] whitespace-nowrap pb-3 pl-4 text-right font-semibold" />
              )}
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((row) => (
              <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                <td className="py-3 font-medium text-gray-900">
                  {row.employee}
                  <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                  <div className="mt-0.5 text-xs font-normal text-gray-500">{[row.store, row.city].filter(Boolean).join(' • ') || '—'}</div>
                </td>
                <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                {variant === 'watchlist' ? (
                  <>
                    <td className="py-3 pl-4 text-right">
                      {/* The watchlist only lists unhappy employees, so the mood
                          severity chip was redundant (and wrapped/broke the cell).
                          Show just the unhappy-day count. */}
                      <span className="whitespace-nowrap font-bold text-rose-500">
                        {(row as LowMoodRow).negativeDays} day{(row as LowMoodRow).negativeDays === 1 ? '' : 's'}
                      </span>
                    </td>
                    <td className="py-3 pl-4">
                      {(row as LowMoodRow).latestMood ? (
                        <div className="flex items-start gap-1.5">
                          <span
                            className="mt-1.5 h-2 w-2 flex-shrink-0 rounded-full"
                            style={{ backgroundColor: MOOD_HEX[(row as LowMoodRow).latestMood as MoodKey] }}
                          />
                          {/* Date stacked UNDER the label, not inline — keeps this cell
                              narrow so the card's 6 columns fit at half-width. */}
                          <div className="min-w-0">
                            <div className="whitespace-nowrap text-gray-800">{(row as LowMoodRow).latestMoodLabel}</div>
                            <div className="text-xs text-gray-400">{fmtMoodDate((row as LowMoodRow).latestMoodDate)}</div>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 pl-4 text-right font-semibold text-gray-900">{(row as LowMoodRow).avg6mScore == null ? '—' : (row as LowMoodRow).avg6mScore}</td>
                    <td className="py-3 pl-4 text-right">
                      {onOpenRemarks && (
                        <button
                          type="button"
                          onClick={() => onOpenRemarks(row.emp_code, row.employee)}
                          className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                          title="View / add remarks"
                        >
                          <MessageSquare className="h-3.5 w-3.5" />
                          {remarkCounts?.[row.emp_code] ? <span className="font-semibold text-primary-600">{remarkCounts[row.emp_code]}</span> : 'Add'}
                        </button>
                      )}
                    </td>
                  </>
                ) : (
                  <td className="py-3 pl-4 text-right">
                    <span className="font-medium text-gray-500">Not logged today</span>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}

      {!loading && !error && total > 10 && <div className="mt-2 text-right text-xs text-gray-400">Top 10 of {total}</div>}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// View-all modals

function LowMoodModal({ rows, onClose, remarkCounts, onOpenRemarks }: {
  rows: LowMoodRow[];
  onClose: () => void;
  remarkCounts?: Record<string, number>;
  onOpenRemarks?: (code: string, name: string) => void;
}) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.employee, r.emp_code, r.store, r.city, r.mobile].some((v) => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const sort = useTableSort(filtered, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
    unhappyDays: (r) => r.negativeDays,
    avgMood: (r) => r.avgScore,
    latest: (r) => r.latestMoodDate,
    score: (r) => r.monthScore,
    avg6m: (r) => r.avg6mScore,
    // Remarks = the remark count on the button ("Add" = none yet = 0).
    remarks: (r) => (onOpenRemarks ? (remarkCounts?.[r.emp_code] ?? 0) : null),
  });

  return (
    <Modal onClose={onClose} title="Low-mood watchlist">
      <div className="mb-3">
        <SearchBox value={search} onChange={setSearch} placeholder="Search by name, code, store, city, mobile…" />
      </div>
      <div className="mb-2 text-xs text-gray-500">{filtered.length} of {rows.length} employees</div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No matching employees.</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
                <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Unhappy days" sortKey="unhappyDays" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Avg mood" sortKey="avgMood" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Latest" sortKey="latest" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Score" sortKey="score" sort={sort} align="right" title="Selected-month scorecard score" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="6-mo Avg" sortKey="avg6m" sort={sort} align="right" title="6-month average scorecard score" className="whitespace-nowrap pb-3 pl-4 font-semibold" />
                <SortTh label="Remarks" sortKey="remarks" sort={sort} align="right" className="whitespace-nowrap pb-3 pl-4 text-right font-semibold" />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row) => (
                <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-medium text-gray-900">
                    {row.employee}
                    <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">{[row.store, row.city].filter(Boolean).join(' • ') || '—'}</div>
                  </td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                  <td className="py-3 pl-4 text-right font-bold text-rose-500">{row.negativeDays}</td>
                  <td className="py-3 pl-4 text-right text-gray-700">{row.avgScore == null ? '—' : `${row.avgScore} / 5`}</td>
                  <td className="whitespace-nowrap py-3 pl-4 text-right">
                    <MoodBadge moodKey={row.latestMood} label={row.latestMoodLabel || ''} />
                    {row.latestMoodDate && <span className="ml-1.5 text-xs text-gray-400">{fmtMoodDate(row.latestMoodDate)}</span>}
                  </td>
                  <td className="py-3 pl-4 text-right font-semibold text-gray-900">{row.monthScore == null ? '—' : row.monthScore}</td>
                  <td className="py-3 pl-4 text-right text-gray-700">{row.avg6mScore == null ? '—' : row.avg6mScore}</td>
                  <td className="py-3 pl-4 text-right">
                    {onOpenRemarks && (
                      <button
                        type="button"
                        onClick={() => onOpenRemarks(row.emp_code, row.employee)}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50"
                        title="View / add remarks"
                      >
                        <MessageSquare className="h-3.5 w-3.5" />
                        {remarkCounts?.[row.emp_code] ? <span className="font-semibold text-primary-600">{remarkCounts[row.emp_code]}</span> : 'Add'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

function NotSubmittedModal({ rows, onClose }: { rows: NotSubmittedRow[]; onClose: () => void }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => [r.employee, r.emp_code, r.store, r.city, r.mobile].some((v) => (v || '').toLowerCase().includes(q)));
  }, [rows, search]);

  const sort = useTableSort(filtered, {
    employee: (r) => r.employee,
    mobile: (r) => r.mobile,
  });

  return (
    <Modal onClose={onClose} title="Not submitted today">
      <div className="mb-3">
        <SearchBox value={search} onChange={setSearch} placeholder="Search by name, code, store, city, mobile…" />
      </div>
      <div className="mb-2 text-xs text-gray-500">{filtered.length} of {rows.length} employees</div>
      {filtered.length === 0 ? (
        <div className="py-10 text-center text-sm text-gray-500">No matching employees.</div>
      ) : (
        <div className="max-h-[55vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white">
              <tr className="border-b border-gray-100 text-left text-xs uppercase tracking-wider text-gray-400">
                <SortTh label="Employee" sortKey="employee" sort={sort} className="pb-3 font-semibold" />
                <SortTh label="Mobile Number" sortKey="mobile" sort={sort} className="whitespace-nowrap pb-3 pl-4 font-semibold" />
              </tr>
            </thead>
            <tbody>
              {sort.sorted.map((row) => (
                <tr key={row.emp_code} className="border-b border-gray-50 hover:bg-gray-50/50">
                  <td className="py-3 font-medium text-gray-900">
                    {row.employee}
                    <span className="ml-1 text-xs text-gray-400">({row.emp_code})</span>
                    <div className="mt-0.5 text-xs font-normal text-gray-500">{[row.store, row.city].filter(Boolean).join(' • ') || '—'}</div>
                  </td>
                  <td className="whitespace-nowrap py-3 pl-4 text-gray-700">{row.mobile || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
